// Import the RTK Query methods from the React-specific entry point
import { createApi } from "@reduxjs/toolkit/query/react";
import {
  Account,
  AccountLink,
  AccountPrivilege,
  BatchJobLog,
  CalendarEvent,
  Campaign,
  City,
  ContentBlock,
  Country,
  CreateAccountValues,
  Currency,
  Customer,
  CustomImage,
  DraftOrderInvite,
  EmailContent,
  Favorite,
  Feedback,
  MailingList,
  NotificationType,
  PaginatedList,
  PaginationContext,
  Participant,
  ParticipantContentBlock,
  ParticipantUser,
  PaymentAccount,
  PaymentDestination,
  Permission,
  PosLocation,
  PosStatus,
  Product,
  ProductCategory,
  ProductSaleNotificationSubscription,
  Province,
  ReferralFeeAmounts,
  ReferralTransaction,
  ReferredAccountTotal,
  Review,
  SeatAllocation,
  SeatingChart,
  Subscriber,
  SubscriptionActivity,
  Survey,
  SurveyResult,
  Template,
  TicketGroup,
  Tip,
  UnsubscribeStats,
  User,
  VariantPaymentAmounts,
  Venue,
  VenueNotificationSubscription,
} from "@models/models";
import {
  Cart,
  Discount,
  Order,
  ProductVariant,
  Region,
  Return,
  StoreCustomersRes,
  StorePostCustomersReq,
} from "@medusajs/medusa";
import { baseQueryWithReauth } from "@store/customBaseQuery";
import { PaginationInput } from "@hooks/pagination";
import { productTransformer } from "../transformers/getProduct";
import { VenueValues } from "@components/forms/venue-form-fields";
import { UnknownCountInput, UnknownCountList } from "@hooks/unknown-count-list";
import { JoinTicketGroupValues } from "@app/[locale]/b/[domain]/store/join-group/[ticketGroupId]/page";
import { CityValues } from "@components/city-form-fields";
import { FindOrderValues } from "@app/[locale]/admin/(default)/analytics/orders/find/page";
import { UpdateLinkValues } from "@app/[locale]/admin/(default)/events/[productId]/draft-orders/page";
import { CreateDraftOrderValues } from "@app/[locale]/b/[domain]/store/claim-tickets/[code]/page";
import { GetSingleReviewValues, ReviewValues } from "@components/rating";
import { CampaignValues } from "@components/campaign-form-fields";
import { ContentBlockValues } from "@components/content-block-form-fields";
import { ProductNotificationValues } from "@components/product-notification-form-fields";
import { TemplateValues } from "@components/template-form-fields";
import { ReserveSeatValues } from "@components/seat-selector";
import {
  BlockSeatsValues,
  SeatPricesValues,
} from "@components/variant-seating-manager";
import { additionalStoreProductsRelations } from "@utils/get-products-helpers";
import { AccountPrivilegeValues } from "@components/account-privilege-form-fields";
import { ParticipantContentBlockValues } from "@components/participant-profile";
import { SwapValues } from "@app/[locale]/admin/(default)/analytics/orders/[orderId]/swap/page";
import { SubscriberValues } from "@components/edit-subscriber";
import { GroupTicketsByOrderValues } from "@components/group-tickets-by-order";

// Define our single API slice object
export const apiSlice = createApi({
  // The cache reducer expects to be added at `state.api` (already default - this is optional)
  reducerPath: "api",
  tagTypes: ["Venue", "Products", "Product", "GiftCards"],
  // All of our requests will have URLs starting with '/fakeApi'
  baseQuery: baseQueryWithReauth,
  // The "endpoints" represent operations and requests for this server
  endpoints: (builder) => ({
    getCustomerUser: builder.mutation<Customer, string>({
      query: (token) => ({
        url: "/store/customer-user",
        body: { token },
        method: "POST",
      }),
      transformResponse: (response: { customer: Customer }) =>
        response.customer,
    }),
    getAdminUserByToken: builder.mutation<User, string>({
      query: (token) => ({
        url: "/store/admin-user",
        body: { token },
        method: "POST",
      }),
      transformResponse: (response: { user: User }) => response.user,
    }),
    logoutAdminUser: builder.mutation<void, void>({
      query: () => ({
        url: `/admin/auth`,
        method: "DELETE",
      }),
    }),
    logoutCustomer: builder.mutation<void, void>({
      query: () => ({
        url: `/store/auth`,
        method: "DELETE",
      }),
    }),
    getAdminUsers: builder.query<User[], void>({
      query: () => `/admin/users?limit=1000`,
      transformResponse: (response: { users: [] }) => response.users,
    }),
    getOrdersForLoggedInAdminUser: builder.query<
      PaginatedList<Order>,
      PaginationInput & { accountId: string }
    >({
      query: (input) => ({
        url: `/admin/orders`,
        params: input,
      }),
      transformResponse: (response: { items: [] } & PaginationContext) => ({
        ...response,
        items: response.items,
      }),
    }),
    getOrdersByCustomerId: builder.query<
      PaginatedList<Order>,
      { input: PaginationInput; customerId: string }
    >({
      query: (obj) =>
        `/admin/customers/${obj.customerId}/orders?limit=${obj.input.limit}&offset=${obj.input.offset}`,
    }),
    getCustomerOrders: builder.query<
      { orders: Order[]; count: number },
      { input: PaginationInput }
    >({
      query: (obj) =>
        `/store/authenticated/orders?limit=${obj.input.limit}&offset=${obj.input.offset}`,
    }),
    getCustomers: builder.query<PaginatedList<Customer>, PaginationInput>({
      query: (input) =>
        `/admin/customers/current-user?limit=${input.limit}&offset=${input.offset}`,
      transformResponse: (response: { items: [] } & PaginationContext) => ({
        ...response,
        items: response.items,
      }),
    }),
    getProductSalesSummary: builder.query<
      PaginatedList<any>,
      PaginationInput & { accountId: string }
    >({
      query: (input) =>
        `/admin/analytics/products/sales/summary?accountId=${input.accountId}&limit=${input.limit}&offset=${input.offset}`,
    }),
    getCurrencies: builder.query<Currency[], void>({
      query: () => "/admin/currencies?limit=150",
      transformResponse: (response: { currencies: any }) => response.currencies,
    }),
    getStatsForProducts: builder.mutation<
      { order_count: number; tickets_sold_count: number }[],
      string[]
    >({
      query: (product_ids) => ({
        url: "/admin/products/stats/order-stats",
        body: { product_ids },
        method: "POST",
      }),
    }),
    getDigitalProducts: builder.query<
      PaginatedList<Product>,
      PaginationInput & { account_id: string }
    >({
      query: (input) => ({
        url: "/admin/digital-products",
        params: input,
      }),
    }),
    getEventsForCalendar: builder.query<CalendarEvent[], string>({
      query: (accountId) => ({
        url: `/admin/products/calendar?accountId=${accountId}`,
      }),
      transformResponse: (response: { data: CalendarEvent[] }) => response.data,
    }),
    getProduct: builder.query<Product, { product_id: string }>({
      query: (payload) => `/admin/products/${payload.product_id}`,
      transformResponse: (product: Product) => {
        if (product.type_id === "product_type_digital_product") {
          return product;
        } else {
          return productTransformer(product);
        }
      },
    }),
    getProductByEncryptedId: builder.query<Product, string>({
      query: (encryptedProductId) =>
        `/store/products/encrypted-product-id/${encryptedProductId}`,
      transformResponse: (response: { data: Product }) => response.data,
    }),
    deleteProduct: builder.mutation({
      query: (productId) => ({
        url: `/admin/products/${productId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Products"],
    }),
    getProductVariant: builder.query<ProductVariant, string>({
      query: (productVariantId) =>
        `/store/variants/${productVariantId}?expand=seatingChart,product`,
      transformResponse: (response: { variant: ProductVariant }) =>
        response.variant,
    }),
    getOrderByCartId: builder.query<Order, string>({
      query: (cartId) =>
        `/store/orders/cart/${cartId}?expand=items.variant.product,items.variant.product.account,customer`,
      transformResponse: (response: { order: Order }) => response.order,
    }),
    getOrderDetail: builder.query<Order, string>({
      query: (orderId) =>
        `/store/orders/${orderId}?expand=items.variant.product,region,customer,discounts,gift_cards,notes,returns`,
      transformResponse: (response: { order: Order }) => response.order,
    }),
    getVenue: builder.query<Venue, string>({
      query: (venueId) => `/store/venues/${venueId}`,
    }),
    updateVenue: builder.mutation<Venue, VenueValues>({
      query: (venueValues) => ({
        url: `/admin/venues/${venueValues.id}`,
        method: "POST",
        body: venueValues,
      }),
      invalidatesTags: ["Venue"],
    }),
    getPaymentAccount: builder.query<PaymentAccount, void>({
      query: () => `/admin/c/stripe/payment-account`,
    }),
    savePaymentAccount: builder.mutation({
      query: (paymentAccount: PaymentAccount) => ({
        url: `/admin/c/stripe/payment-account`,
        method: "POST",
        body: paymentAccount,
      }),
    }),
    createTransfer: builder.mutation({
      query: (payload) => ({
        url: `/admin/stripe/transfer`,
        method: "POST",
        body: payload,
      }),
    }),
    getParticipants: builder.query<ParticipantUser[], void>({
      query: () => `/admin/participants`,
      transformResponse: (response: { data: ParticipantUser[] }) =>
        response.data,
    }),
    createParticipant: builder.mutation({
      query: (payload) => ({
        url: `/admin/participants`,
        method: "POST",
        body: payload,
      }),
    }),
    updateParticipant: builder.mutation({
      query: (payload) => ({
        url: `/admin/participants/${payload.id}`,
        method: "POST",
        body: payload,
      }),
    }),
    getParticipant: builder.query<ParticipantUser, string>({
      query: (id) => `/admin/participants/${id}`,
    }),
    createTipPaymentIntent: builder.mutation({
      query: (payload) => ({
        url: `/store/stripe/tips/payment-intent`,
        method: "POST",
        body: payload,
      }),
      transformResponse: (response: { data: any }) => response.data,
    }),
    updateTip: builder.mutation<
      Tip,
      { tipId: string; name: string; note: string }
    >({
      query: (payload) => ({
        url: `/store/tips/${payload.tipId}`,
        method: "POST",
        body: { name: payload.name, note: payload.note },
      }),
      transformResponse: (response: { data: Tip }) => response.data,
    }),
    cancelTipPaymentIntent: builder.mutation({
      query: (payload) => ({
        url: `/store/stripe/tips/payment-intent`,
        method: "DELETE",
        body: payload,
      }),
    }),
    getCountries: builder.query<Country[], void>({
      query: (id) => `/admin/countries`,
    }),
    getProvinces: builder.query<Province[], void>({
      query: (id) => `/admin/provinces`,
    }),
    getTransfersForConnectedAccount: builder.query<
      UnknownCountList<any>,
      UnknownCountInput & any
    >({
      query: (input) =>
        `/admin/payment-destinations/${input.paymentDestinationId}/transfers` +
        (input.offset ? `?offset=${input.offset}` : ""),
    }),
    sendEmailByProductVariant: builder.mutation({
      query: (payload) => ({
        url: `/admin/email/send-by-product-variant`,
        method: "POST",
        body: payload,
      }),
    }),
    sendEmailByProduct: builder.mutation({
      query: (payload) => ({
        url: `/admin/email/send-by-product`,
        method: "POST",
        body: payload,
      }),
    }),
    refundOrderItems: builder.mutation({
      query: (payload) => ({
        url: `/admin/orders/${payload.orderId}/claims`,
        method: "POST",
        body: {
          type: "refund",
          refund_amount: payload.refundAmount,
          claim_items: payload.claimItems,
        },
      }),
    }),
    returnOrderItems: builder.mutation({
      query: (payload) => ({
        url: `/admin/orders/${payload.orderId}/return`,
        method: "POST",
        body: {
          receive_now: true,
          items: payload.returnItems,
          no_notification: payload.no_notification
            ? payload.no_notification
            : undefined,
        },
      }),
    }),
    getCustomerCountByVariantId: builder.query<any, string>({
      query: (variantId) => `/admin/variants/${variantId}/customers/count`,
    }),
    getCustomerCountByProductId: builder.query<any, string>({
      query: (productId) => `/admin/products/${productId}/customers/count`,
    }),
    getRegions: builder.query<Region[], void>({
      query: () => `/store/regions`,
      transformResponse: (response: { regions: Region[] }) => response.regions,
    }),
    followVenue: builder.mutation({
      query: (venueId) => ({
        url: `/store/follow/venue`,
        method: "POST",
        body: {
          venueId,
        },
      }),
    }),
    unfollowVenue: builder.mutation({
      query: (venueId) => ({
        url: `/store/follow/venue`,
        method: "DELETE",
        body: {
          venueId,
        },
      }),
    }),
    getVenueNotificationSettings: builder.query<
      VenueNotificationSubscription[],
      void
    >({
      query: () =>
        `/store/authenticated/notification-subscriptions/venue-events`,
    }),
    getVenueNotificationTypes: builder.query<NotificationType[], void>({
      query: () => `/store/notification-types?category=venue`,
    }),
    updateVenueSubscription: builder.mutation({
      query: (payload) => ({
        url: `/store/authenticated/notification-subscriptions/venue-events/${payload.venueId}`,
        method: "POST",
        body: {
          notificationTypeId: payload.notificationTypeId,
          subscribed: payload.subscribed,
        },
      }),
    }),
    getAccounts: builder.query<Account[], void>({
      query: () => `/admin/accounts`,
    }),
    getTimezones: builder.query<string[], void>({
      query: () => `/admin/timezones`,
    }),
    getOrdersByProductId: builder.query<Order[], string>({
      query: (productId) => `/admin/products/${productId}/orders`,
    }),
    getDiscountsByProductId: builder.query<Discount[], string>({
      query: (productId) => `/admin/products/${productId}/discounts`,
    }),
    getDiscountsByAccountId: builder.query<Discount[], string>({
      query: (accountId) => `/admin/accounts/${accountId}/discounts`,
      transformResponse: (response: { data: Discount[] }) => response.data,
    }),
    addDiscountByProductId: builder.mutation({
      query: (payload) => ({
        url: `/admin/discounts`,
        method: "POST",
        body: payload,
      }),
    }),
    deleteDiscount: builder.mutation({
      query: (discountId) => ({
        url: `/admin/discounts/${discountId}`,
        method: "DELETE",
      }),
    }),
    updateDiscount: builder.mutation({
      query: (payload) => ({
        url: `/admin/discounts/${payload.discountId}`,
        method: "POST",
        body: payload.body,
      }),
    }),
    getDiscount: builder.query<Discount, string>({
      query: (discountId) => `/admin/discounts/${discountId}`,
      transformResponse: (response: { discount: Discount }) =>
        response.discount,
    }),
    getProductCategoriesForUser: builder.query<
      ProductCategory[],
      { topLevel: boolean }
    >({
      query: (query = { topLevel: false }) =>
        `/admin/product-categories?topLevel=${query.topLevel}`,
    }),
    getProductCategoriesForAccount: builder.query<ProductCategory[], string>({
      query: (accountId) => `/admin/product-categories?accountId=${accountId}`,
    }),
    getAccount: builder.query<Account, string>({
      query: (accountId) => `/admin/accounts/${accountId}`,
    }),
    getProductCategory: builder.query<ProductCategory, string>({
      query: (productCategoryId) =>
        `/admin/product-categories/${productCategoryId}`,
      transformResponse: (response: { product_category: ProductCategory }) =>
        response.product_category,
    }),
    addProductCategory: builder.mutation({
      query: (payload) => ({
        url: `/admin/product-categories`,
        method: "POST",
        body: { ...payload },
      }),
      transformResponse: (response: { product_category: ProductCategory }) =>
        response.product_category,
    }),
    updateProductCategory: builder.mutation({
      query: (payload) => ({
        url: `/admin/product-categories/${payload.productCategoryId}`,
        method: "POST",
        body: {
          name: payload.name,
          handle: payload.handle,
          description: payload.description,
        },
      }),
      transformResponse: (response: { product_category: ProductCategory }) =>
        response.product_category,
    }),
    spoofUser: builder.mutation({
      query: (userId) => ({
        url: `/admin/users/spoof`,
        method: "POST",
        body: { spoofedUserId: userId },
      }),
    }),
    getProductDetail: builder.query<
      Product,
      { productId: string; regionId: string; cart_id: string }
    >({
      query: (params) =>
        `/store/products/${params.productId}?cart_id=${params.cart_id}&region_id=${params.regionId}&expand=${additionalStoreProductsRelations.join(",")}`,
      transformResponse: (response: { product: Product }) => {
        if (response.product.type_id === "product_type_digital_product") {
          return response.product;
        } else {
          return productTransformer(response.product);
        }
      },
    }),
    getPaymentAccounts: builder.query<User, void>({
      query: () => `/admin/users/current`,
    }),
    getAllParticipants: builder.query<ParticipantUser[], void>({
      query: () => `/store/participants`,
    }),
    getAllAccounts: builder.query<Account[], void>({
      query: () => `/admin/c/accounts/all`,
      transformResponse: (response: { data: Account[] }) => response.data,
    }),
    getAccountLinksNew: builder.mutation<AccountLink, CreateAccountValues>({
      query: (payload) => ({
        url: `/admin/stripe/accounts/${payload.accountId}/account-links`,
        method: "POST",
        body: payload,
      }),
    }),
    enablePaymentDestination: builder.mutation<PaymentDestination, string>({
      query: (paymentDestinationId) => ({
        url: `/admin/payment-destinations/${paymentDestinationId}/enable`,
        method: "POST",
      }),
    }),
    deletePaymentDestination: builder.mutation<PaymentDestination, string>({
      query: (paymentDestinationId) => ({
        url: `/admin/payment-destinations/${paymentDestinationId}`,
        method: "DELETE",
      }),
    }),
    markAsTippingPaymentDestination: builder.mutation<
      PaymentDestination,
      string
    >({
      query: (paymentDestinationId) => ({
        url: `/admin/payment-destinations/${paymentDestinationId}/mark-for-tipping`,
        method: "POST",
      }),
    }),
    cloneEvent: builder.mutation<Product, string>({
      query: (productId) => ({
        url: `/admin/products/${productId}/clone`,
        method: "POST",
      }),
    }),
    inviteParticipant: builder.mutation<
      Participant,
      { participantName: string; participantEmail: string }
    >({
      query: (payload) => ({
        url: `/admin/participants/invite`,
        method: "POST",
        body: payload,
      }),
    }),
    getTicketGroup: builder.query<TicketGroup, string>({
      query: (id) => `/store/ticket-groups/${id}`,
    }),
    joinTicketGroup: builder.mutation<any, JoinTicketGroupValues>({
      query: (payload) => ({
        url: `/store/ticket-groups/${payload.ticketGroupId}/join`,
        method: "POST",
        body: payload,
      }),
    }),
    getCities: builder.query<City[], void>({
      query: () => `/store/cities`,
    }),
    getCity: builder.query<City, string>({
      query: (id) => `/store/cities/${id}`,
    }),
    addCity: builder.mutation<City, CityValues>({
      query: (payload) => ({
        url: `/admin/cities`,
        method: "POST",
        body: payload,
      }),
    }),
    updateCity: builder.mutation<City, CityValues>({
      query: (payload) => ({
        url: `/admin/cities/${payload.id}`,
        method: "POST",
        body: payload,
      }),
    }),
    getAbandonedCarts: builder.query<Cart[], string>({
      query: (account_id) => `/admin/abandoned-carts?account_id=${account_id}`,
      transformResponse: (response: { data: Cart[] }) => response.data,
    }),
    getAbandonedCartEmail: builder.query<EmailContent, string>({
      query: (cartId) => `/admin/abandoned-carts/${cartId}/email`,
    }),
    sendAbandonedCartEmail: builder.mutation<
      { status: string },
      EmailContent & { cartId: string }
    >({
      query: (payload) => ({
        url: `/admin/abandoned-carts/${payload.cartId}/email`,
        method: "POST",
        body: { subject: payload.subject, body: payload.body },
      }),
    }),
    getPublicProductSalesSummary: builder.query<any, string>({
      query: (encryptedProductId) =>
        `/store/analytics/products/${encryptedProductId}/summary`,
    }),
    getSingleProductSalesSummary: builder.query<any, string>({
      query: (productId) => `/admin/products/${productId}/sales-summary`,
      transformResponse: (response: { data: any[] }) => response.data,
    }),
    encryptString: builder.query<{ value: string }, string>({
      query: (str) => `/admin/encrypt/${str}`,
    }),
    findOrders: builder.query<PaginatedList<Order>, FindOrderValues>({
      query: (values) => {
        return { url: `/admin/orders`, params: values };
      },
    }),
    addProductCategoryToAccount: builder.mutation<
      ProductCategory,
      { name: string; accountId: string; handle: string }
    >({
      query: (payload) => ({
        url: `/admin/product-categories`,
        method: "POST",
        body: payload,
      }),
    }),
    updateDraftOrderInvite: builder.mutation<
      DraftOrderInvite,
      UpdateLinkValues
    >({
      query: (payload) => ({
        url: `/admin/comp-ticket-invites/${payload.id}`,
        method: "POST",
        body: payload,
      }),
    }),
    getDraftOrderInviteVariants: builder.query<
      {
        variants: ProductVariant[];
        defaultTicketsPerShowPerEmail: number | undefined;
      },
      string
    >({
      query: (code) => {
        return { url: `/store/comp-ticket-invites/code/${code}` };
      },
    }),
    createDraftOrder: builder.mutation<Order, CreateDraftOrderValues>({
      query: (payload) => ({
        url: `/store/comp-ticket-claims`,
        method: "POST",
        body: payload,
      }),
    }),
    getDraftOrderInvites: builder.query<DraftOrderInvite[], string>({
      query: (productId) => {
        return { url: `/admin/products/${productId}/comp-ticket-invites` };
      },
    }),
    createCustomer: builder.mutation<StoreCustomersRes, StorePostCustomersReq>({
      query: (payload) => ({
        url: `/store/customers`,
        method: "POST",
        body: payload,
      }),
    }),
    getReturns: builder.query<PaginatedList<Return>, string>({
      query: (status) => `/admin/returns?status=${status}`,
    }),
    getReviews: builder.query<PaginatedList<Review>, string>({
      query: (productId) => ({ url: `/admin/products/${productId}/reviews` }),
    }),
    getCustomerReviews: builder.query<PaginatedList<Review>, void>({
      query: () => `/store/authenticated/reviews`,
    }),
    postReview: builder.mutation<Review, ReviewValues>({
      query: (payload) => ({
        url: `/store/reviews`,
        method: "POST",
        body: payload,
      }),
    }),
    getSingleReview: builder.mutation<Review, GetSingleReviewValues>({
      query: (payload) => ({
        url: `/store/reviews/lookup`,
        method: "POST",
        body: payload,
      }),
    }),
    getCampaign: builder.query<Campaign, string>({
      query: (campaignId) => ({
        url: `/admin/campaigns/${campaignId}`,
        params: cacheBusterQueryParams(),
      }),
    }),

    createCampaign: builder.mutation<Campaign, CampaignValues>({
      query: (payload) => ({
        url: `/admin/campaigns`,
        method: "POST",
        body: payload,
      }),
    }),
    updateCampaign: builder.mutation<Campaign, CampaignValues>({
      query: (payload) => ({
        url: `/admin/campaigns/${payload.id}`,
        method: "POST",
        body: payload,
      }),
    }),
    deleteCampaign: builder.mutation<void, string>({
      query: (campaignId) => ({
        url: `/admin/campaigns/${campaignId}`,
        method: "DELETE",
      }),
    }),
    createMailingList: builder.mutation<
      MailingList,
      { name: string; accountId: string }
    >({
      query: (payload) => ({
        url: `/admin/mailing-lists`,
        method: "POST",
        body: payload,
      }),
    }),
    updateCampaignStatus: builder.mutation<
      Campaign,
      { id: string; status: string }
    >({
      query: (payload) => ({
        url: `/admin/campaigns/${payload.id}/publish`,
        method: "POST",
        body: { status: payload.status },
      }),
    }),
    createFeedback: builder.mutation<Feedback, Feedback>({
      query: (payload) => ({
        url: `/admin/feedback`,
        method: "POST",
        body: payload,
      }),
    }),
    getSite: builder.query<any, string>({
      query: (id) => ({ url: `/store/c/sites/${id}` }),
    }),
    createContentBlockForProduct: builder.mutation<
      ContentBlock,
      ContentBlockValues
    >({
      query: (payload) => ({
        url: `/admin/cms/content-blocks/products/${payload.productId}`,
        method: "POST",
        body: payload,
        transformResponse: (response: { data: ContentBlock }) => response.data,
      }),
    }),
    updateContentBlock: builder.mutation<ContentBlock, ContentBlockValues>({
      query: (payload) => ({
        url: `/admin/cms/content-blocks/${payload.id}`,
        method: "PUT",
        body: payload,
        transformResponse: (response: { data: ContentBlock }) => response.data,
      }),
    }),
    toggleContentBlock: builder.mutation<ContentBlock, string>({
      query: (contentBlockId) => ({
        url: `/admin/cms/content-blocks/${contentBlockId}/toggle`,
        method: "POST",
      }),
      transformResponse: (response: { data: ContentBlock }) => response.data,
    }),
    getContentBlock: builder.mutation<ContentBlock, string>({
      query: (id) => ({ url: `/admin/cms/content-blocks/${id}` }),
      transformResponse: (response: { data: ContentBlock }) => response.data,
    }),
    deleteContentBlock: builder.mutation<void, string>({
      query: (id) => ({
        url: `/admin/cms/content-blocks/${id}`,
        method: "DELETE",
      }),
    }),
    getProductSaleNotificationSubscriptions: builder.query<
      ProductSaleNotificationSubscription[],
      string
    >({
      query: (productId) => ({
        url: `/admin/notification-subscriptions/product-sales/${productId}`,
      }),
      transformResponse: (response: {
        data: ProductSaleNotificationSubscription[];
      }) => response.data,
    }),
    createProductSaleNotification: builder.mutation<
      ProductSaleNotificationSubscription,
      ProductNotificationValues
    >({
      query: (payload) => ({
        url: `/admin/notification-subscriptions/product-sales/${payload.productId}`,
        method: "POST",
        body: payload,
        transformResponse: (response: {
          data: ProductSaleNotificationSubscription;
        }) => response.data,
      }),
    }),
    deleteNotificationSubscription: builder.mutation<void, string>({
      query: (id) => ({
        url: `/admin/notification-subscriptions/${id}`,
        method: "DELETE",
      }),
    }),
    getTemplate: builder.query<Template, string>({
      query: (id) => ({ url: `/admin/templates/${id}` }),
      transformResponse: (response: { data: Template }) => response.data,
    }),
    parseTemplate: builder.mutation<string, any>({
      query: (payload) => ({
        url: `/admin/templates/${payload.id}/replace-vars`,
        method: "POST",
        body: payload,
      }),
      transformResponse: (response: { data: string }) => response.data,
    }),
    sendHtmlEmail: builder.mutation<
      void,
      { subject: string; email: string; content: string }
    >({
      query: (payload) => ({
        url: `/admin/email`,
        method: "POST",
        body: payload,
      }),
    }),
    updateTemplate: builder.mutation<Template, TemplateValues>({
      query: (payload) => ({
        url: `/admin/templates/${payload.id}`,
        method: "PUT",
        body: payload,
      }),
      transformResponse: (response: { data: Template }) => response.data,
    }),
    downloadPdfByOrderId: builder.mutation<string, string>({
      query(orderId) {
        return {
          url: `/store/authenticated/orders/${orderId}/download-tickets`,
          method: "POST",
          responseHandler: async (response) =>
            window.URL.createObjectURL(await response.blob()),
          cache: "no-cache",
        };
      },
    }),
    publicDownloadPdfByOrderId: builder.mutation<
      string,
      { order_id: string; ticket_ids?: string[] }
    >({
      query(payload) {
        return {
          url: `/store/orders/${payload.order_id}/download-tickets`,
          method: "POST",
          responseHandler: async (response) =>
            window.URL.createObjectURL(await response.blob()),
          cache: "no-cache",
          body: payload.ticket_ids
            ? { ticket_ids: payload.ticket_ids }
            : undefined,
        };
      },
    }),
    uploadSingleImage: builder.mutation({
      query: (payload) => ({
        url: "/admin/images",
        method: "POST",
        body: payload,
        formData: true,
      }),
    }),
    getImages: builder.query<CustomImage[], void>({
      query: (type) => ({ url: `/admin/images` }),
      transformResponse: (response: { data: CustomImage[] }) => response.data,
    }),
    sendTestEmail: builder.mutation({
      query: (payload) => ({
        url: `/admin/campaigns/${payload.campaignId}/send-test-email`,
        method: "POST",
        body: { email: payload.email },
      }),
    }),
    getSeatingCharts: builder.query<SeatingChart[], string>({
      query: (accountId) => ({
        url: `/admin/accounts/${accountId}/seating-charts`,
      }),
      transformResponse: (response: { data: SeatingChart[] }) => response.data,
    }),
    getSeatingChartTemplates: builder.query<SeatingChart[], void>({
      query: () => ({ url: `/admin/seating-charts` }),
      transformResponse: (response: { data: SeatingChart[] }) => response.data,
    }),
    createSeatingChartFromTemplate: builder.mutation<
      SeatingChart,
      { accountId: string; seatingChartId: string }
    >({
      query: (payload) => ({
        url: `/admin/seating-charts/${payload.seatingChartId}/clone`,
        method: "POST",
        body: { accountId: payload.accountId },
      }),
      transformResponse: (response: { data: SeatingChart }) => response.data,
    }),
    createSeatingChart: builder.mutation({
      query: (payload) => ({
        url: `/admin/seating-charts`,
        method: "POST",
        body: { name: payload.name, accountId: payload.accountId },
      }),
      transformResponse: (response: { data: SeatingChart }) => response.data,
    }),
    getSeatingChart: builder.query<SeatingChart, string>({
      query: (id) => ({
        url: `/store/seating-charts/${id}?c=${Math.random()}`,
      }),
      transformResponse: (response: { data: SeatingChart }) => response.data,
    }),
    updateSeatingChart: builder.mutation<SeatingChart, any>({
      query: (payload) => ({
        url: `/admin/seating-charts/${payload.id}`,
        method: "POST",
        body: payload,
      }),
      transformResponse: (response: { data: SeatingChart }) => response.data,
    }),
    getSeatAllocations: builder.mutation<
      SeatAllocation[],
      { cartId?: string; variantId: string }
    >({
      query: (payload) => ({
        url: `/store/seats/allocations`,
        method: "POST",
        body: payload,
      }),
      transformResponse: (response: { data: SeatAllocation[] }) =>
        response.data,
    }),
    reserveSeats: builder.mutation<Cart, ReserveSeatValues>({
      query: (payload) => ({
        url: `/store/seats/reserve`,
        method: "POST",
        body: payload,
      }),
      transformResponse: (response: { data: Cart }) => response.data,
    }),
    blockSeats: builder.mutation<void, BlockSeatsValues>({
      query: (payload) => ({
        url: `/admin/seats/block`,
        method: "POST",
        body: payload,
      }),
    }),
    unblockSeats: builder.mutation<void, BlockSeatsValues>({
      query: (payload) => ({
        url: `/admin/seats/unblock`,
        method: "POST",
        body: payload,
      }),
    }),
    updateSeatPrices: builder.mutation<void, SeatPricesValues>({
      query: (payload) => ({
        url: `/admin/seats/prices`,
        method: "POST",
        body: payload,
      }),
    }),
    getGiftCardProductsToManage: builder.query<
      PaginatedList<Product>,
      PaginationInput & { accountId: string }
    >({
      query: (input) => ({
        url: `/admin/gift-card-products?query=${Math.random()}`,
        params: input,
      }),
      providesTags: ["GiftCards"],
    }),
    getPosLocationByLocationIdentifier: builder.mutation<PosLocation, string>({
      query: (identifier) => ({
        url: "/store/pos/locations",
        method: "POST",
        body: { identifier },
      }),
      transformResponse: (response: { data: PosLocation }) => response.data,
    }),
    sendPaymentPromptToPos: builder.mutation<PosStatus, string>({
      query: (cartId) => ({
        url: "/store/pos/send",
        method: "POST",
        body: { cartId },
      }),
      transformResponse: (response: { data: PosStatus }) => response.data,
    }),
    simulatePayment: builder.mutation<any, string>({
      query: (readerId) => ({
        url: "/store/stripe/simulate-payment",
        method: "POST",
        body: { readerId },
      }),
    }),
    getPosCartStatus: builder.mutation<PosStatus, string>({
      query: (cartId) => ({
        url: "/store/pos/cart-status",
        method: "POST",
        body: { cartId },
      }),
      transformResponse: (response: { data: PosStatus }) => response.data,
    }),
    getAccountPrivileges: builder.query<AccountPrivilege[], string>({
      query: (accountId) => ({
        url: `/admin/accounts/${accountId}/privileges`,
      }),
      transformResponse: (response: { data: AccountPrivilege[] }) =>
        response.data,
    }),
    deleteAccountPrivilege: builder.mutation<void, string>({
      query: (id) => ({
        url: `/admin/privileges/${id}`,
        method: "DELETE",
      }),
    }),
    updateAccountPrivilege: builder.mutation<
      AccountPrivilege,
      AccountPrivilegeValues
    >({
      query: (payload) => ({
        url: `/admin/privileges/${payload.privilegeId}`,
        method: "POST",
        body: { permissionId: payload.permissionId },
      }),
      transformResponse: (response: { data: AccountPrivilege }) =>
        response.data,
    }),
    addAccountPrivilege: builder.mutation<
      AccountPrivilege,
      AccountPrivilegeValues
    >({
      query: (payload) => ({
        url: `/admin/privileges`,
        method: "POST",
        body: {
          email: payload.email,
          accountId: payload.accountId,
          permissionId: payload.permissionId,
        },
      }),
      transformResponse: (response: { data: AccountPrivilege }) =>
        response.data,
    }),
    getPayableReferralFeeAmounts: builder.mutation<
      ReferralFeeAmounts[],
      string
    >({
      query: (accountId) => ({
        url: `/admin/referrals/payable-amounts`,
        method: "POST",
        body: { accountId },
      }),
      transformResponse: (response: { data: ReferralFeeAmounts[] }) =>
        response.data,
    }),
    getReferralTransactions: builder.mutation<
      PaginatedList<ReferralTransaction>,
      PaginationInput & { accountId: string }
    >({
      query: (payload) => ({
        url: `/admin/referrals/transactions`,
        method: "POST",
        body: payload,
      }),
      transformResponse: (response: {
        data: PaginatedList<ReferralTransaction>;
      }) => response.data,
    }),
    getReferredAccountsSummary: builder.mutation<
      ReferredAccountTotal[],
      string
    >({
      query: (accountId) => ({
        url: `/admin/referrals/referred-account-summary`,
        method: "POST",
        body: { accountId },
      }),
      transformResponse: (response: { data: ReferredAccountTotal[] }) =>
        response.data,
    }),
    resendVerificationEmail: builder.mutation<any, string>({
      query: (sub) => ({
        url: `/store/auth/resend-verification-email`,
        method: "POST",
        body: { sub },
      }),
    }),
    createMassRefundForProductVariant: builder.mutation<any, string>({
      query: (payload) => ({
        url: `/admin/variants/mass-refund`,
        method: "POST",
        body: payload,
      }),
    }),
    checkInventory: builder.query<boolean, string>({
      query: (cartId) => ({
        url: `/store/carts/${cartId}/check-inventory?c=${Math.random()}`,
      }),
      transformResponse: (response: { data: boolean }) => response.data,
    }),
    getBatchJobLogs: builder.query<BatchJobLog[], string>({
      query: (batchJobId) => ({ url: `/admin/batch-jobs/${batchJobId}/logs` }),
      transformResponse: (response: { data: BatchJobLog[] }) => response.data,
    }),
    submitSurvey: builder.mutation<any, any>({
      query: (payload) => ({
        url: `/store/surveys/${payload.surveyId}`,
        method: "POST",
        body: payload,
      }),
      transformResponse: (response: { data: boolean }) => response.data,
    }),
    getSurveys: builder.query<PaginatedList<Survey>, string>({
      query: (accountId) => ({ url: `/admin/surveys?accountId=${accountId}` }),
      transformResponse: (response: { data: PaginatedList<Survey> }) =>
        response.data,
    }),
    getSurvey: builder.query<Survey, string>({
      query: (surveyId) => ({ url: `/store/surveys/${surveyId}` }),
      transformResponse: (response: { data: Survey }) => response.data,
    }),
    getSurveyResults: builder.query<SurveyResult[], string>({
      query: (surveyId) => ({ url: `/admin/surveys/${surveyId}/results` }),
      transformResponse: (response: { data: SurveyResult[] }) => response.data,
    }),
    downloadSurveyResults: builder.query<string, string>({
      query(surveyId) {
        return {
          url: `/admin/surveys/${surveyId}/results/download`,
          responseHandler: async (response) =>
            window.URL.createObjectURL(await response.blob()),
          cache: "no-cache",
        };
      },
    }),
    saveReviewEmail: builder.mutation<Account, any>({
      query: (payload) => ({
        url: `/admin/accounts/${payload.accountId}/review-email`,
        method: "POST",
        body: payload,
      }),
      transformResponse: (response: { data: Account }) => response.data,
    }),
    getUnsubscribeStats: builder.query<UnsubscribeStats, string>({
      query: (mailingListId) => ({
        url: `/admin/mailing-lists/${mailingListId}/unsubscribe-metrics`,
      }),
      transformResponse: (response: { data: UnsubscribeStats }) =>
        response.data,
    }),
    sendCustomerTickets: builder.mutation<
      void,
      { order_id: string; ticket_ids?: string[]; email?: string; note?: string }
    >({
      query: (payload) => ({
        url: `/admin/orders/${payload.order_id}/send-customer-tickets`,
        method: "POST",
        body: { ...payload, order_id: undefined },
      }),
    }),
    addParticipantContentBlock: builder.mutation<
      ParticipantContentBlock,
      ParticipantContentBlockValues
    >({
      query: (payload) => ({
        url: `/admin/cms/content-blocks/participants/${payload.participantId}`,
        method: "POST",
        body: payload,
      }),
      transformResponse: (response: { data: any }) => response.data,
    }),
    deleteParticipantContentBlock: builder.mutation<void, string>({
      query: (participantContentBlockId) => ({
        url: `/admin/cms/participant-content-blocks/${participantContentBlockId}`,
        method: "DELETE",
      }),
    }),
    getParticipantContentBlocks: builder.query<
      ParticipantContentBlock[],
      string
    >({
      query: (participantId) => ({
        url: `/admin/cms/content-blocks/participants/${participantId}`,
      }),
      transformResponse: (response: { data: ParticipantContentBlock[] }) =>
        response.data,
    }),
    getOpenGraphInfo: builder.mutation<any, string>({
      query: (url) => ({
        url: `/admin/open-graph`,
        method: "POST",
        body: { url },
      }),
      transformResponse: (response: { data: any }) => response.data,
    }),
    reorderParticipantContentBlock: builder.mutation<
      ParticipantContentBlock,
      {
        participantContentBlockId: string;
        direction: string;
      }
    >({
      query: (payload) => ({
        url: `/admin/cms/participant-content-blocks/${payload.participantContentBlockId}/reorder`,
        method: "POST",
        body: { direction: payload.direction },
      }),
      transformResponse: (response: { data: ParticipantContentBlock }) =>
        response.data,
    }),
    addFavorite: builder.mutation<
      Favorite,
      { resourceId: string; resourceType: string }
    >({
      query: (payload) => ({
        url: `/admin/favorites`,
        method: "POST",
        body: payload,
      }),
      transformResponse: (response: { data: Favorite }) => response.data,
    }),
    deleteFavorite: builder.mutation<void, string>({
      query: (favoriteId) => ({
        url: `/admin/favorites/${favoriteId}`,
        method: "DELETE",
      }),
    }),
    getFavorites: builder.query<{ products: Product[] }, void>({
      query: () => ({ url: `/admin/favorites` }),
      transformResponse: (response: { data: { products: Product[] } }) =>
        response.data,
    }),
    updateProductThumbnail: builder.mutation<
      void,
      { productId: string; thumbnail: string }
    >({
      query: (payload) => ({
        url: `/admin/products/${payload.productId}/thumbnail`,
        method: "POST",
        body: { thumbnail: payload.thumbnail },
      }),
    }),
    swapTickets: builder.mutation<Order, SwapValues>({
      query: (payload) => ({
        url: `/admin/swap-tickets`,
        method: "POST",
        body: payload,
      }),
      transformResponse: (response: { data: Order }) => response.data,
    }),
    saveSubscriber: builder.mutation<Subscriber, SubscriberValues>({
      query: (payload) => ({
        url: `/admin/subscribers/${payload.subscriberId}`,
        method: "POST",
        body: payload,
      }),
      transformResponse: (response: { data: Subscriber }) => response.data,
    }),
    getSubscriptionActivities: builder.query<SubscriptionActivity[], string>({
      query: (subscriberId) => ({
        url: `/admin/subscribers/${subscriberId}/activities`,
      }),
      transformResponse: (response: { data: SubscriptionActivity[] }) =>
        response.data,
    }),
    getProfitSharingVariantPaymentAmounts: builder.query<
      VariantPaymentAmounts[],
      string
    >({
      query: (accountId) => ({
        url: `/admin/accounts/${accountId}/profit-share/summary`,
      }),
      transformResponse: (response: { data: VariantPaymentAmounts[] }) =>
        response.data,
    }),
    groupTicketsByOrder: builder.mutation<void, GroupTicketsByOrderValues>({
      query: (payload) => ({
        url: `/admin/tickets/group-tickets`,
        method: "POST",
        body: payload,
      }),
    }),
    sendTicketsFromLastOrderByEmail: builder.mutation<{ data: string }, string>(
      {
        query: (email) => ({
          url: `/store/tickets/latest`,
          method: "POST",
          body: { email },
        }),
      },
    ),
    updatePriceForPayWhatYouLikeLineItem: builder.mutation<
      any,
      { line_item_id: string; price: number }
    >({
      query: (payload) => ({
        url: `/store/line-items/${payload.line_item_id}/pay-what-you-like`,
        method: "POST",
        body: { price: payload.price },
      }),
    }),
    addGiftCardCodesByOrderId: builder.mutation<
      any,
      { cart_id: string; gift_card_codes: string[] }
    >({
      query: (payload) => ({
        url: `/store/carts/${payload.cart_id}/add-gift-card-codes`,
        method: "POST",
        body: { gift_card_codes: payload.gift_card_codes },
      }),
    }),
    addToGuestList: builder.mutation<
      any,
      {
        variant_id: string;
        first_name: string;
        last_name: string;
        number_of_tickets: number;
      }
    >({
      query: (payload) => ({
        url: `/admin/comp-ticket-invites/add-to-guest-list`,
        method: "POST",
        body: payload,
      }),
    }),
    createProduct: builder.mutation<Product, CreateProductRequest>({
      query: (payload) => ({
        url: `/admin/v2/products`,
        method: "POST",
        body: payload,
      }),
      transformResponse: (response: { data: Product }) => response.data,
    }),
    createDigitalProduct: builder.mutation<
      Product,
      CreateDigitalProductRequest
    >({
      query: (payload) => ({
        url: `/admin/digital-products`,
        method: "POST",
        body: payload,
      }),
      transformResponse: (response: { data: Product }) => response.data,
    }),
    createGiftCardProduct: builder.mutation<Product, CreateGiftCardRequest>({
      query: (payload) => ({
        url: `/admin/v2/gift-card-products`,
        method: "POST",
        body: payload,
      }),
      transformResponse: (response: { data: Product }) => response.data,
    }),
    addGiftCardDenomination: builder.mutation<
      ProductVariant,
      AddGiftCardDenominationRequest
    >({
      query: (payload) => ({
        url: `/admin/gift-card-products/${payload.product_id}/denominations`,
        method: "POST",
        body: { ...payload, product_id: undefined },
      }),
      transformResponse: (response: { data: ProductVariant }) => response.data,
    }),
    deleteGiftCardDenomination: builder.mutation<{ data: string }, string>({
      query: (variant_id) => ({
        url: `/admin/gift-card-products/denominations`,
        method: "DELETE",
        body: { variant_id },
      }),
    }),
    updateGiftCardProductCategoryRestrictions: builder.mutation<
      { data: string },
      UpdateGiftCardProductCategoryRestrictionsRequest
    >({
      query: (payload) => ({
        url: `/admin/gift-card-products/${payload.product_id}/restrictions`,
        method: "POST",
        body: { ...payload, product_id: undefined },
      }),
    }),
    getReferralLinkOrders: builder.query<CurrencyResults, number>({
      query: (referral_link_id) => ({
        url: `/admin/referral-links/${referral_link_id}/orders`,
      }),
      transformResponse: (response: { data: CurrencyResults }) => response.data,
    }),
    getOrdersByDiscountId: builder.query<Order[], string>({
      query: (discount_id) => ({
        url: `/admin/discounts/${discount_id}/orders`,
      }),
      transformResponse: (response: { data: Order[] }) => response.data,
    }),
    sendPostShowTestEmail: builder.mutation<
      { data: string },
      { variant_id: string; email: string }
    >({
      query: (payload) => ({
        url: `/admin/variants/${payload.variant_id}/send-post-show-test-email`,
        method: "POST",
        body: { email: payload.email },
      }),
    }),
    updateProductAttribute: builder.mutation<
      { data: string },
      { product_id: string; name: string; value: string }
    >({
      query: (payload) => ({
        url: `/admin/products/${payload.product_id}/attributes`,
        method: "POST",
        body: { name: payload.name, value: payload.value },
      }),
    }),
    updateVariantAttribute: builder.mutation<
      { data: string },
      { variant_id: string; name: string; value: string | null }
    >({
      query: (payload) => ({
        url: `/admin/variants/${payload.variant_id}/attributes`,
        method: "POST",
        body: { name: payload.name, value: payload.value },
      }),
    }),
    addVariant: builder.mutation<
      ProductVariant,
      { product_id: string; starts_at: string }
    >({
      query: (payload) => ({
        url: `/admin/products/${payload.product_id}/variants`,
        method: "POST",
        body: { starts_at: payload.starts_at },
      }),
      transformResponse: (response: { data: ProductVariant }) => response.data,
    }),
    deleteVariant: builder.mutation<{ data: string }, string>({
      query: (variant_id) => ({
        url: `/admin/variants/${variant_id}`,
        method: "DELETE",
      }),
    }),
    deleteStoreProductCache: builder.mutation<void, string>({
      query: (id) => ({
        url: `/admin/products/${id}/cache`,
        method: "DELETE",
      }),
    }),
    reprocessOrder: builder.mutation<void, string>({
      query: (id) => ({
        url: `/admin/ops/orders/${id}/order-placed-event`,
        method: "POST",
      }),
    }),
    deleteOrderCache: builder.mutation<void, string>({
      query: (id) => ({
        url: `/admin/orders/${id}/cache`,
        method: "DELETE",
      }),
    }),
    swapPartialTickets: builder.mutation<
      void,
      { ticket_ids: string[]; variant_id: string }
    >({
      query: (params) => ({
        url: `/admin/swap-partial-tickets`,
        method: "POST",
        body: params,
      }),
    }),
  }),
});

// Export the auto-generated hook for the `getPosts` query endpoint
export const {
  useGetAdminUserByTokenMutation,
  useSwapPartialTicketsMutation,
  useGetStatsForProductsMutation,
  useUpdateGiftCardProductCategoryRestrictionsMutation,
  useDeleteGiftCardDenominationMutation,
  useAddGiftCardDenominationMutation,
  useCreateGiftCardProductMutation,
  useCreateDigitalProductMutation,
  useLazyGetDigitalProductsQuery,
  useDeleteOrderCacheMutation,
  useReprocessOrderMutation,
  useDeleteStoreProductCacheMutation,
  useDeleteVariantMutation,
  useAddVariantMutation,
  useUpdateVariantAttributeMutation,
  useUpdateProductAttributeMutation,
  useSendPostShowTestEmailMutation,
  useGetOrdersByDiscountIdQuery,
  useLazyGetReferralLinkOrdersQuery,
  useCreateProductMutation,
  useAddToGuestListMutation,
  useAddGiftCardCodesByOrderIdMutation,
  useSendTicketsFromLastOrderByEmailMutation,
  useGetProductByEncryptedIdQuery,
  useGroupTicketsByOrderMutation,
  useLazyGetProfitSharingVariantPaymentAmountsQuery,
  useDeleteCampaignMutation,
  useGetAllParticipantsQuery,
  useLazyGetAllParticipantsQuery,
  useLazyGetSubscriptionActivitiesQuery,
  useSaveSubscriberMutation,
  useSwapTicketsMutation,
  useUpdateProductThumbnailMutation,
  useGetFavoritesQuery,
  useAddFavoriteMutation,
  useDeleteFavoriteMutation,
  useReorderParticipantContentBlockMutation,
  useToggleContentBlockMutation,
  useDeleteParticipantContentBlockMutation,
  useGetOpenGraphInfoMutation,
  useLazyGetParticipantContentBlocksQuery,
  useAddParticipantContentBlockMutation,
  useUpdateTipMutation,
  useLazyGetEventsForCalendarQuery,
  useCreateSeatingChartFromTemplateMutation,
  useLazyGetSeatingChartTemplatesQuery,
  useSendCustomerTicketsMutation,
  useLazyDownloadSurveyResultsQuery,
  useGetSurveyQuery,
  useLazyGetSurveysQuery,
  useGetSurveyResultsQuery,
  useGetUnsubscribeStatsQuery,
  useSaveReviewEmailMutation,
  useSubmitSurveyMutation,
  useLazyGetBatchJobLogsQuery,
  useLazyCheckInventoryQuery,
  useCreateMassRefundForProductVariantMutation,
  useResendVerificationEmailMutation,
  usePublicDownloadPdfByOrderIdMutation,
  useGetSingleProductSalesSummaryQuery,
  useGetReferredAccountsSummaryMutation,
  useGetReferralTransactionsMutation,
  useGetPayableReferralFeeAmountsMutation,
  useUpdateAccountPrivilegeMutation,
  useAddAccountPrivilegeMutation,
  useDeleteAccountPrivilegeMutation,
  useLazyGetAccountPrivilegesQuery,
  useGetPosCartStatusMutation,
  useSimulatePaymentMutation,
  useSendPaymentPromptToPosMutation,
  useGetPosLocationByLocationIdentifierMutation,
  useGetAllAccountsQuery,
  useLazyGetGiftCardProductsToManageQuery,
  useLazyGetProductCategoriesForAccountQuery,
  useUpdateSeatPricesMutation,
  useUnblockSeatsMutation,
  useBlockSeatsMutation,
  useReserveSeatsMutation,
  useGetSeatAllocationsMutation,
  useUpdateSeatingChartMutation,
  useLazyGetSeatingChartQuery,
  useCreateSeatingChartMutation,
  useLazyGetSeatingChartsQuery,
  useSendTestEmailMutation,
  useLazyGetImagesQuery,
  useUploadSingleImageMutation,
  useDownloadPdfByOrderIdMutation,
  useUpdateTemplateMutation,
  useSendHtmlEmailMutation,
  useParseTemplateMutation,
  useLazyGetTemplateQuery,
  useGetTemplateQuery,
  useDeleteNotificationSubscriptionMutation,
  useCreateProductSaleNotificationMutation,
  useGetProductSaleNotificationSubscriptionsQuery,
  useDeleteContentBlockMutation,
  useGetContentBlockMutation,
  useUpdateContentBlockMutation,
  useCreateContentBlockForProductMutation,
  useCreateFeedbackMutation,
  useDeletePaymentDestinationMutation,
  useLazyGetAccountQuery,
  useUpdateCampaignStatusMutation,
  useUpdateCampaignMutation,
  useGetCampaignQuery,
  useLazyGetCampaignQuery,
  useCreateCampaignMutation,
  useGetCustomerReviewsQuery,
  useGetSingleReviewMutation,
  usePostReviewMutation,
  useLazyGetCustomerOrdersQuery,
  useGetReturnsQuery,
  useLazyGetOrderDetailQuery,
  useGetOrderDetailQuery,
  useCreateCustomerMutation,
  useGetDraftOrderInvitesQuery,
  useCreateDraftOrderMutation,
  useGetDraftOrderInviteVariantsQuery,
  useUpdateDraftOrderInviteMutation,
  useAddProductCategoryToAccountMutation,
  useLazyFindOrdersQuery,
  useEncryptStringQuery,
  useGetPublicProductSalesSummaryQuery,
  useSendAbandonedCartEmailMutation,
  useLazyGetAbandonedCartEmailQuery,
  useLazyGetAbandonedCartsQuery,
  useLazyGetCityQuery,
  useUpdateCityMutation,
  useAddCityMutation,
  useGetCitiesQuery,
  useJoinTicketGroupMutation,
  useLazyGetTicketGroupQuery,
  useInviteParticipantMutation,
  useCloneEventMutation,
  useGetPaymentAccountsQuery,
  useEnablePaymentDestinationMutation,
  useMarkAsTippingPaymentDestinationMutation,
  useGetAccountLinksNewMutation,
  useLazyGetPaymentAccountsQuery,
  useLazyGetProductDetailQuery,
  useSpoofUserMutation,
  useUpdateProductCategoryMutation,
  useGetProductCategoryQuery,
  useGetProductCategoriesForUserQuery,
  useLazyGetDiscountQuery,
  useGetDiscountQuery,
  useGetRegionsQuery,
  useLazyGetRegionsQuery,
  useLazyGetDiscountsByProductIdQuery,
  useLazyGetDiscountsByAccountIdQuery,
  useAddDiscountByProductIdMutation,
  useDeleteDiscountMutation,
  useUpdateDiscountMutation,
  useLazyGetOrdersByProductIdQuery,
  useGetTimezonesQuery,
  useGetAccountsQuery,
  useUpdateVenueSubscriptionMutation,
  useGetVenueNotificationTypesQuery,
  useGetVenueNotificationSettingsQuery,
  useUnfollowVenueMutation,
  useFollowVenueMutation,
  useLazyGetCustomerCountByVariantIdQuery,
  useLazyGetCustomerCountByProductIdQuery,
  useGetCountriesQuery,
  useGetProvincesQuery,
  useSendEmailByProductVariantMutation,
  useSendEmailByProductMutation,
  useLazyGetTransfersForConnectedAccountQuery,
  useCancelTipPaymentIntentMutation,
  useCreateTipPaymentIntentMutation,
  useGetVenueQuery,
  useUpdateVenueMutation,
  useLazyGetParticipantQuery,
  useUpdateParticipantMutation,
  useCreateParticipantMutation,
  useGetParticipantsQuery,
  useCreateTransferMutation,
  useDeleteProductMutation,
  useGetAdminUsersQuery,
  useLogoutCustomerMutation,
  useGetCustomerUserMutation,
  useLogoutAdminUserMutation,
  useGetCurrenciesQuery,
  useLazyGetOrdersForLoggedInAdminUserQuery,
  useLazyGetCustomersQuery,
  useLazyGetOrdersByCustomerIdQuery,
  useLazyGetProductVariantQuery,
  useLazyGetProductQuery,
  useLazyGetOrderByCartIdQuery,
  useLazyGetProductSalesSummaryQuery,
  endpoints,
} = apiSlice;

function cacheBusterQueryParams(): { cache: number } {
  return { cache: Math.random() };
}

export type CreateProductRequest = {
  title: string;
  place_id: string;
  showtime: string;
  is_virtual: boolean;
  account_id: string;
  currency_code: string;
  amount: number;
  inventory_quantity: number;
};

export type CreateGiftCardRequest = {
  title: string;
  account_id: string;
  currency_code: string;
  amount: number;
  is_pass: boolean;
};

export type CreateDigitalProductRequest = {
  title: string;
  account_id: string;
  currency_code: string;
  amount: number;
};

type ReferralLinkOrderSummary = {
  id: string;
  display_id: number;
  total: number;
};

type CurrencyResult = {
  orders: ReferralLinkOrderSummary[];
  currency: string;
  total: number;
};

export type CurrencyResults = {
  currencies: CurrencyResult[];
};

export type AddGiftCardDenominationRequest = {
  product_id: string;
  amount: number;
  currency_code: string;
};

export type UpdateGiftCardProductCategoryRestrictionsRequest = {
  product_id: string;
  category_ids: string[];
};
